import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/ui/card";
import { Badge } from "../Components/ui/badge";
import { Button } from "../Components/ui/button";
import { Input } from "../Components/ui/input";
import { Label } from "../Components/ui/label";
import { Loader2, User, Mail, Phone, Award, Users, Package, CheckCircle, TrendingUp, ThumbsUp, ThumbsDown, LayoutDashboard, Layers, Settings, MapPin } from "lucide-react";
import apiClient from "../api/apiClient";
import { format } from 'date-fns';
import { useToast } from "../context/ToastContext";
import { getAdminStats } from "../api/adminApi";

// API functions for the new workflow
const getPendingDonations = async () => {
  const { data } = await apiClient.get("/admin/pending-donations");
  return data;
};

const approveDonation = async (donationId) => {
  const { data } = await apiClient.post(`/admin/donations/${donationId}/approve`);
  return data;
};

const rejectDonation = async (donationId) => {
  const { data } = await apiClient.post(`/admin/donations/${donationId}/reject`);
  return data;
};

const PendingDonations = () => {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const { data: pendingDonations, isLoading } = useQuery({
    queryKey: ["pendingDonations"],
    queryFn: getPendingDonations,
  });

  const approveMutation = useMutation({
    mutationFn: approveDonation,
    onSuccess: () => {
      queryClient.invalidateQueries(["pendingDonations"]);
      queryClient.invalidateQueries(["admin-dashboard-stats"]); // Also refresh stats
    },
    onError: (err) => setError(err.response?.data?.msg || "Failed to approve."),
  });

  const rejectMutation = useMutation({
    mutationFn: rejectDonation,
    onSuccess: () => {
      queryClient.invalidateQueries(["pendingDonations"]);
    },
    onError: (err) => setError(err.response?.data?.msg || "Failed to reject."),
  });

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-400" /></div>;
  }

  return (
    <Card className="backdrop-blur-sm bg-white/50 dark:bg-zinc-900/50 border-zinc-200 dark:border-zinc-800 shadow-xl overflow-hidden">
      <CardHeader className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
        <CardTitle className="text-xl text-zinc-900 dark:text-white flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5 text-blue-500" />
          Pending Donation Approvals
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {error && <p className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 p-3 rounded-md mb-4 border border-red-200 dark:border-red-900/50">{error}</p>}
        {pendingDonations && pendingDonations.length > 0 ? (
          <div className="grid gap-4">
            {pendingDonations.map((donation) => (
              <div key={donation._id} className="group p-5 border border-zinc-200 dark:border-zinc-800 rounded-xl bg-zinc-50 dark:bg-zinc-950/50 hover:bg-white dark:hover:bg-zinc-900/80 transition-all duration-200 hover:border-zinc-300 dark:hover:border-zinc-700 shadow-sm">
                <div className="flex flex-col lg:flex-row gap-6 justify-between">
                  <div className="space-y-4 flex-1">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <User className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-zinc-900 dark:text-white text-lg">{donation.donor?.name || 'Unknown Donor'}</h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {donation.donor?.email || 'N/A'}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {donation.donor?.phone_number || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 pl-14">
                      <div className="bg-white dark:bg-zinc-900/50 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800/50">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1">Donation Type</p>
                        <p className="text-zinc-700 dark:text-zinc-200 capitalize font-medium">{donation.donation_type}</p>
                      </div>
                      {donation.amount && (
                        <div className="bg-white dark:bg-zinc-900/50 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800/50">
                          <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1">Amount</p>
                          <p className="text-zinc-700 dark:text-zinc-200 font-medium">${donation.amount}</p>
                        </div>
                      )}
                      {donation.items && donation.items.length > 0 && (
                        <div className="bg-white dark:bg-zinc-900/50 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800/50 sm:col-span-2">
                          <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1">Items</p>
                          <p className="text-zinc-700 dark:text-zinc-200 text-sm">
                            {donation.items.map(i => `${i.name} (x${i.quantity})`).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-zinc-500 pl-14">
                      Submitted on {format(new Date(donation.createdAt), 'PPP')}
                    </p>
                  </div>

                  <div className="flex lg:flex-col items-center lg:items-end justify-end gap-3 min-w-[140px]">
                    <Button
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-900/20"
                      onClick={() => approveMutation.mutate(donation._id)}
                      disabled={approveMutation.isPending}
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-white"
                      onClick={() => rejectMutation.mutate(donation._id)}
                      disabled={rejectMutation.isPending}
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-zinc-100 dark:bg-zinc-950/30 rounded-xl border border-zinc-200 dark:border-zinc-800/50 border-dashed">
            <CheckCircle className="w-12 h-12 text-zinc-400 dark:text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-600 dark:text-zinc-500 font-medium">No pending approvals</p>
            <p className="text-zinc-500 dark:text-zinc-600 text-sm mt-1">All caught up! New donations will appear here.</p>
          </div>
        )}
      </CardContent>
    </Card >
  );
};


export default function AdminDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch pools for the Active Pools card
  const { data: pools } = useQuery({
    queryKey: ['pools'],
    queryFn: async () => {
      const { data } = await apiClient.get('/pools');
      return data;
    },
  });

  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ["admin-dashboard-stats"],
    queryFn: getAdminStats,
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (isError || !stats) {
    return <div className="min-h-screen p-6 bg-zinc-50 dark:bg-zinc-950 text-red-500 flex items-center justify-center">Failed to load admin stats.</div>;
  }

  return (
    <div className="p-6 lg:p-8 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-zinc-200 dark:border-zinc-900">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-white tracking-tight">
              Dashboard
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-2 flex items-center gap-2">
              Welcome back, <span className="text-zinc-700 dark:text-zinc-200 font-medium">{user?.name || user?.full_name || 'Admin'}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-4 py-1.5 text-sm bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-500 dark:text-zinc-400 font-normal">
              {format(new Date(), 'EEEE, MMMM do, yyyy')}
            </Badge>
          </div>
        </div>

        {/* Admin Info Card (Restored) */}
        {/* Admin Dashboard Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">

          {/* Left Column: Admin Profile Card */}
          <Card className="col-span-1 border-none shadow-2xl bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-700 text-white overflow-hidden relative min-h-[320px] flex flex-col items-center text-center p-1 rounded-3xl">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/10 rounded-full blur-3xl opacity-60"></div>
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-56 h-56 bg-black/20 rounded-full blur-3xl opacity-60"></div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%20200%20200%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cfilter%20id%3D%22noiseFilter%22%3E%3CfeTurbulence%20type%3D%22fractalNoise%22%20baseFrequency%3D%220.65%22%20numOctaves%3D%223%22%20stitchTiles%3D%22stitch%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20filter%3D%22url%28%23noiseFilter%29%22%2F%3E%3C%2Fsvg%3E')] opacity-20 brightness-100"></div>

            <CardContent className="relative z-10 flex flex-col items-center justify-between w-full h-full p-8">
              <div className="flex flex-col items-center w-full">
                <div className="relative mb-6">
                  <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-md p-1.5 shadow-xl ring-4 ring-white/10">
                    <div className="w-full h-full rounded-full bg-gradient-to-tr from-white to-zinc-200 flex items-center justify-center border-2 border-white/50">
                      <User className="w-10 h-10 text-indigo-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 bg-emerald-400 rounded-full border-4 border-indigo-700"></div>
                </div>

                <div className="space-y-2 mb-6">
                  <h3 className="text-2xl font-bold tracking-tight leading-snug">{user?.name}</h3>
                  <p className="text-blue-100/80 text-sm font-medium tracking-wide flex items-center justify-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> {user?.email}
                  </p>
                </div>

                <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/10 backdrop-blur-md px-4 py-1.5 text-xs uppercase tracking-widest font-bold shadow-sm">
                  {user?.role || 'Administrator'}
                </Badge>
              </div>

              <div className="w-full mt-8 pt-6 border-t border-white/10 flex flex-col gap-3">
                <div className="flex justify-between items-center text-xs font-medium text-blue-200/80">
                  <span className="uppercase tracking-wider">Admin ID</span>
                  <span className="font-mono bg-black/20 px-2 py-0.5 rounded text-white/90 text-[10px] break-all">{user?._id}</span>
                </div>
                <div className="flex justify-between items-center text-xs font-medium text-blue-200/80">
                  <span className="uppercase tracking-wider">Status</span>
                  <span className="flex items-center gap-1.5 text-emerald-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    Active
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column: Stats & Pending Actions */}
          <div className="lg:col-span-3 space-y-8">

            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[
                  { label: "Active Donors", value: stats.activeDonors, icon: Users, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-900/20", border: "border-blue-100 dark:border-blue-800/50", link: "/donorslist" },
                  { label: "Active Batch Staff", value: stats.activeBatchStaff, icon: Users, color: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-100 dark:border-emerald-800/50", link: "/batchstafflist" },
                  { label: "Batches to Assign", value: stats.toBeAssigned, icon: Package, color: "text-orange-600", bg: "bg-orange-50 dark:bg-orange-900/20", border: "border-orange-100 dark:border-orange-800/50", link: "/donation-requests" },
                  { label: "Delivered Batches", value: stats.delivered, icon: CheckCircle, color: "text-pink-600", bg: "bg-pink-50 dark:bg-pink-900/20", border: "border-pink-100 dark:border-pink-800/50", link: "/donation-requests" },
                  { label: "Active Pools", value: pools?.filter(p => p.status === 'active').length || 0, icon: Layers, color: "text-violet-600", bg: "bg-violet-50 dark:bg-violet-900/20", border: "border-violet-100 dark:border-violet-800/50", link: "/pools" }
                ].map((stat, idx) => (
                  <Card
                    key={idx}
                    className={`border ${stat.border} shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group relative overflow-hidden bg-white dark:bg-zinc-900`}
                    onClick={() => navigate(stat.link)}
                  >
                    <div className="p-5 flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{stat.label}</p>
                        <h4 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white group-hover:scale-105 transition-transform origin-left duration-300">
                          {stat.value}
                        </h4>
                      </div>
                      <div className={`p-3 rounded-2xl ${stat.bg} ${stat.color} group-hover:rotate-6 transition-transform duration-300`}>
                        <stat.icon className="w-6 h-6" />
                      </div>
                    </div>
                    {/* Bottom fade line */}
                    <div className={`absolute bottom-0 left-0 w-full h-1 ${stat.bg.replace('/20', '')}`} />
                  </Card>
                ))}
              </div>
            )}

            {/* Pending Donations Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 px-1">
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
                  <span className="flex h-2 w-2 rounded-full bg-blue-600"></span>
                  Pending Actions
                </h2>
                <Badge variant="secondary" className="bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400 rounded-full px-2.5">
                  {stats?.pendingApprovals || 0}
                </Badge>
              </div>
              <PendingDonations />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}